import { SectionHeader } from '../common/section-header'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useId, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { queryKeys } from '../api/query-keys.js'
import { ContentStatusBadge } from '../activity/content-history-badges.js'
import { formatActivityTimestamp } from '../activity/activity-presentation.js'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  buttonVariants,
  Icon,
  type IconName,
  Notice,
  Textarea,
} from '@speaking-coach/ui'
import { HighConsequenceActionPanel } from '../common/high-consequence-action-panel.js'
import { AdminApiError } from '../api/client.js'
import { OperatorFaqTrigger } from '../operator-faq/operator-faq.js'
import { adminContentClient } from './api.js'
import { ContentDetailShell, ContentKeyBadge, ContentSourceFile } from './content-shell.js'
import type { RevisionDetail, RevisionHistoryEvent, RevisionIdentity, RevisionKind, RevisionLifecycleOperation, RevisionList, RevisionMetadata } from './revision-types.js'

import { readRevisionTutorPresentation, TutorPresentationComparison, TutorPresentationPanel } from './tutor-presentation'

const panel = 'space-y-4 rounded-token-card border border-border-default bg-surface-elev p-5'

function revisionBase(kind: RevisionKind, identityId: string) {
  return `/admin/content/revisions/${kind}/${encodeURIComponent(identityId)}`
}

export function revisionHref(
  kind: RevisionKind,
  identityId: string,
  revisionId?: string,
  returnTo?: string | null,
) {
  const base = revisionBase(kind, identityId)
  const path = revisionId ? `${base}/${encodeURIComponent(revisionId)}` : base
  const params = new URLSearchParams()
  if (returnTo) params.set('returnTo', returnTo)
  return `${path}${params.size ? `?${params}` : ''}`
}

function useRevisionSummary(kind: RevisionKind, identityId: string) {
  return useQuery({
    queryKey: queryKeys.contentRevisions(kind, identityId, 1, 5),
    queryFn: () => adminContentClient.revisions(kind, identityId, 1, 5),
  })
}

export function RevisionSummaryFields({
  kind,
  identityId,
  repositorySourcePath,
}: {
  kind: RevisionKind
  identityId: string
  repositorySourcePath: string | null
}) {
  const location = useLocation()
  const returnTo = `${location.pathname}${location.search}`
  const query = useRevisionSummary(kind, identityId)
  if (query.isPending) return <><dl className="sm:col-span-2"><dt className="text-text-secondary">Content shown on this page</dt><dd className="mt-1 text-text-secondary">Loading revision…</dd></dl><ContentSourceFile value={repositorySourcePath} relationship="unknown" /></>
  if (query.isError) return <><dl className="sm:col-span-2"><dt className="text-text-secondary">Content shown on this page</dt><dd className="mt-1 text-danger-text">Current revision could not be determined.</dd></dl><ContentSourceFile value={repositorySourcePath} relationship="unknown" /></>
  const active = query.data.activeRevision
  const draft = query.data.draftRevision
  const activeReadable = active?.readable === true
  const sourcePath = activeReadable && active.sourceType === 'repository' ? active.sourcePath : repositorySourcePath
  const sourceRelationship = active && active.readable === false
    ? 'legacy-fallback'
    : active?.sourceType === 'repository'
    ? 'direct'
    : active
      ? 'legacy-reference'
      : 'no-active-revision'
  return <><dl data-slot="revision-summary" className={`grid gap-4 sm:col-span-2 ${draft ? 'sm:grid-cols-2' : ''}`}>
    <div>
      <dt className="text-text-secondary">Content shown on this page</dt>
      <dd className="mt-1 text-text-primary">{active?.readable === true ? <><Link className="font-medium underline" to={revisionHref(kind, identityId, active.id, returnTo)}>Revision {active.authorRevision}</Link><span className="text-text-secondary"> · current published revision</span></> : active ? <><span className="font-medium">Retained identity fields</span><span className="text-text-secondary"> · current Revision {active.authorRevision} is unreadable</span></> : <><span className="font-medium">No active revision</span><span className="text-text-secondary"> · retained identity fields are shown for inspection only</span></>}</dd>
    </div>
    {draft ? <div>
      <dt className="flex items-center gap-1.5 font-medium text-warning-text"><Icon name="warning" className="shrink-0" />Unpublished draft remains</dt>
      <dd className="mt-1 text-text-primary"><Link className="font-medium underline" to={revisionHref(kind, identityId, draft.id, returnTo)}>Revision {draft.authorRevision}</Link><span className="text-text-secondary"> · not shown as the current content; check the staging release run</span></dd>
    </div> : null}
  </dl><ContentSourceFile value={sourcePath} relationship={sourceRelationship} /></>
}

function identityHref(identity: RevisionIdentity, returnTo?: string | null): string {
  if (returnTo?.startsWith('/admin/content') || returnTo?.startsWith('/admin/sessions/')) return returnTo
  if (identity.kind === 'tutor') return `/admin/content/tutors/${identity.id}`
  if (identity.scenarioType === 'onboarding') return '/admin/content/scenarios?kind=diagnostic'
  return `/admin/content/scenarios/${identity.id}`
}

function identityReturnLabel(returnTo?: string | null): string {
  return returnTo?.startsWith('/admin/sessions/') ? 'Back to call' : 'Back to content'
}

function compareHref(
  kind: RevisionKind,
  identityId: string,
  from: string,
  to: string,
  returnTo?: string | null,
): string {
  const params = new URLSearchParams({ from, to })
  if (returnTo) params.set('returnTo', returnTo)
  return `${revisionBase(kind, identityId)}/compare?${params}`
}

function publicationLabel(item: RevisionMetadata): string {
  if (item.sourceType === 'legacy-current-snapshot') return 'Legacy snapshot'
  return ({
    active: 'Current published revision',
    unpublished_draft: 'Unpublished draft',
    abandoned_draft: 'Never available to learners',
    published_retired: 'Previously published',
  })[item.publicationState]
}

function rollbackLabel(
  item: RevisionMetadata,
  activeRevision: RevisionMetadata | null,
): string | null {
  if (
    item.publicationState !== 'published_retired' ||
    !activeRevision ||
    activeRevision.authorRevision >= item.authorRevision
  ) return null
  return `Rolled back to Revision ${activeRevision.authorRevision}`
}

function RevisionRow({
  identity,
  item,
  activeRevision,
  returnTo,
  comparisonMode = false,
  selectedForComparison = false,
  showSecondRevisionHint = false,
  onComparisonSelect,
}: {
  identity: RevisionIdentity
  item: RevisionMetadata
  activeRevision: RevisionMetadata | null
  returnTo?: string | null
  comparisonMode?: boolean
  selectedForComparison?: boolean
  showSecondRevisionHint?: boolean
  onComparisonSelect?: (item: RevisionMetadata) => void
}) {
  const detailHref = revisionHref(identity.kind, identity.id, item.id, returnTo)
  const rollback = rollbackLabel(item, activeRevision)
  return <li className={`group relative flex flex-wrap items-center justify-between gap-3 rounded-token-card border bg-surface-app px-4 py-3 transition-colors hover:border-border-strong hover:bg-surface-hover ${selectedForComparison ? 'border-accent ring-2 ring-accent' : 'border-border-default'}`}>
    {comparisonMode ? <button
      aria-label={item.readable === false ? `Revision ${item.authorRevision} cannot be compared` : `Select Revision ${item.authorRevision} for comparison`}
      aria-pressed={selectedForComparison}
      className="absolute inset-0 rounded-token-card focus:outline-none focus-visible:focus-ring disabled:cursor-not-allowed"
      disabled={item.readable === false}
      onClick={() => onComparisonSelect?.(item)}
      type="button"
    /> : <Link
      aria-label={`Open revision ${item.authorRevision}`}
      className="absolute inset-0 rounded-token-card focus:outline-none focus-visible:focus-ring"
      to={detailHref}
    />}
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Revision {item.authorRevision}</span>
        {rollback ? <Badge variant="muted"><Icon name="rollback" size={14} data-slot="revision-rollback-icon" />{rollback}</Badge> : <><ContentStatusBadge status={item.status} /><Badge variant="muted">{publicationLabel(item)}</Badge></>}
        {item.readable === false ? <Badge variant="warning">Unreadable</Badge> : null}
      </div>
      <p className="mt-1 text-caption text-text-secondary">{item.contentHash.slice(0, 12)} · created {formatActivityTimestamp(item.createdAt)} by {item.createdByActorId}</p>
      <p className="mt-1 text-caption text-text-secondary">{item.sourceType === 'legacy-current-snapshot' ? 'Original publication was not recorded' : item.firstPublishedAt ? `First published ${formatActivityTimestamp(item.firstPublishedAt)} by ${item.firstPublishedByActorId}` : item.publicationState === 'abandoned_draft' ? 'Superseded before publication' : 'Never published'}</p>
    </div>
    <div className="pointer-events-none relative z-10 flex items-center gap-2">
      {selectedForComparison ? <Badge variant="neutral">First revision</Badge> : null}
      {showSecondRevisionHint ? <span className="text-caption font-medium text-text-secondary opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">Second revision</span> : null}
      {comparisonMode ? <Icon name="compare" className="text-text-secondary" /> : <Icon name="forward" className="text-text-secondary transition-transform group-hover:translate-x-0.5" />}
    </div>
  </li>
}

function RevisionStateSummary({ data, returnTo }: { data: RevisionList; returnTo: string }) {
  const active = data.activeRevision
  const draft = data.draftRevision
  return <div className="space-y-3">
    <div className="rounded-token-card border border-border-default bg-surface-app px-4 py-3">
      <p className="text-label font-medium uppercase tracking-wide text-text-secondary">Current published</p>
      {active ? <><Link className="mt-1 inline-block font-semibold underline" to={revisionHref(data.identity.kind, data.identity.id, active.id, returnTo)}>Revision {active.authorRevision}</Link><p className="mt-1 text-caption text-text-secondary">Used for new calls while this content is available.</p></> : <p className="mt-1 text-body text-text-secondary">None — new calls cannot select this content.</p>}
    </div>
    {draft ? <Notice variant="warning">
      <p className="font-medium">Unpublished draft remains</p>
      <p className="mt-1"><Link className="font-semibold underline" to={revisionHref(data.identity.kind, data.identity.id, draft.id, returnTo)}>Revision {draft.authorRevision}</Link> is not used by learners. A successful staging release normally publishes its imported draft; inspect the corresponding release run.</p>
    </Notice> : null}
  </div>
}

export function RevisionsSection({ kind, identityId }: { kind: RevisionKind; identityId: string }) {
  const location = useLocation()
  const returnTo = `${location.pathname}${location.search}`
  const query = useRevisionSummary(kind, identityId)
  if (query.isPending) return <section className={panel} aria-label="Content revisions"><p className="text-body text-text-secondary">Loading revisions…</p></section>
  if (query.isError) return <section className={panel} aria-label="Content revisions"><Notice variant="danger">Could not load content revisions.</Notice></section>
  const data = query.data
  const active = data.activeRevision
  const draft = data.draftRevision
  const earlier = data.items.filter(item => item.id !== active?.id && item.id !== draft?.id)
  return <section className={panel} aria-labelledby="content-revisions-heading">
    <SectionHeader
      id="content-revisions-heading"
      title="Revisions"
      meta={<OperatorFaqTrigger topicId="content-revisions" label="How revisions work" />}
      description={"Revision numbers record creation order. A deployment imports and publishes revisions in its environment. Rollback can make an older revision current again."}
      actions={<Link className={buttonVariants({ size: 'sm', variant: 'secondary' })} to={revisionHref(kind, identityId, undefined, returnTo)}><Icon name="resetView" />Browse all {data.total}</Link>}
    />
    <RevisionStateSummary data={data} returnTo={returnTo} />
    {earlier.length ? <div className="space-y-2"><SectionHeader level={3} title="Earlier revisions · newest first" /><ul className="space-y-2">{earlier.map(item => <RevisionRow key={item.id} identity={data.identity} item={item} activeRevision={active} returnTo={returnTo} />)}</ul></div> : data.items.length ? null : <Notice variant="neutral">No retained revisions are recorded for this content.</Notice>}
  </section>
}

function Pager({ page, total, limit, onChange }: { page: number; total: number; limit: number; onChange: (page: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / limit))
  return <nav className="flex items-center justify-between gap-3 text-caption text-text-secondary" aria-label="Section pagination"><span>{total} entries · page {page} of {pages}</span><div className="flex gap-2"><Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => onChange(page - 1)}>Previous</Button><Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => onChange(page + 1)}>Next</Button></div></nav>
}

const historyActions: Record<string, { icon: IconName; label: string }> = {
  'content.revision_imported': { icon: 'import', label: 'Imported' },
  'content.revision_published': { icon: 'success', label: 'Published' },
  'content.revision_republished': { icon: 'success', label: 'Re-published' },
  'content.revision_rolled_back': { icon: 'rollback', label: 'Rolled back' },
  'content.revision_retired': { icon: 'retire', label: 'Retired' },
  'content.revision_restored': { icon: 'restore', label: 'Restored' },
}

function HistoryEvent({ identity, event, returnTo }: { identity: RevisionIdentity; event: RevisionHistoryEvent; returnTo?: string | null }) {
  const action: { icon: IconName; label: string } = historyActions[event.action] ?? {
    icon: 'history',
    label: event.action.replace('content.revision_', '').replaceAll('_', ' '),
  }
  return <li className="rounded-token-card border border-border-default bg-surface-app px-4 py-3 text-body">
    <div className="flex flex-wrap items-center justify-between gap-2"><p className="flex items-center gap-2 font-medium"><Icon name={action.icon} className="shrink-0 text-text-secondary" data-slot={`revision-history-${event.action.replace('content.revision_', '').replaceAll('_', '-')}-icon`} />{action.label}</p><time className="text-caption text-text-secondary">{formatActivityTimestamp(event.occurredAt)}</time></div>
    <p className="mt-1 text-text-secondary">{event.reason ?? 'No reason was recorded.'}</p>
    <p className="mt-2 text-caption text-text-secondary">Actor: {event.actorId}{event.release ? ` · Release: ${event.release}` : ' · No release identifier recorded'}</p>
    <div className="mt-2 flex flex-wrap items-center gap-2 text-caption">
      {event.fromRevisionId ? <Link className="underline" to={revisionHref(identity.kind, identity.id, event.fromRevisionId, returnTo)}>From Revision {event.fromAuthorRevision ?? 'unknown'}</Link> : <span className="text-text-secondary">No previous revision</span>}
      <Icon name="forward" size={12} />
      {event.toRevisionId ? <Link className="underline" to={revisionHref(identity.kind, identity.id, event.toRevisionId, returnTo)}>To Revision {event.toAuthorRevision ?? 'unknown'}</Link> : <span className="text-text-secondary">No active revision</span>}
    </div>
  </li>
}

export function ContentRevisionListPage() {
  const { kind = 'lesson', identityId = '' } = useParams<{ kind: RevisionKind; identityId: string }>()
  const revisionKind = kind === 'tutor' ? 'tutor' : 'lesson'
  const [search, setSearch] = useSearchParams()
  const navigate = useNavigate()
  const [comparisonMode, setComparisonMode] = useState(false)
  const [comparisonStart, setComparisonStart] = useState<RevisionMetadata | null>(null)
  const page = Math.max(1, Number(search.get('page')) || 1)
  const historyPage = Math.max(1, Number(search.get('historyPage')) || 1)
  const returnTo = search.get('returnTo')
  const revisions = useQuery({ queryKey: queryKeys.contentRevisions(revisionKind, identityId, page, 20), queryFn: () => adminContentClient.revisions(revisionKind, identityId, page, 20) })
  const history = useQuery({ queryKey: queryKeys.contentRevisionHistory(revisionKind, identityId, historyPage, 20), queryFn: () => adminContentClient.revisionHistory(revisionKind, identityId, historyPage, 20) })
  const identity = revisions.data?.identity ?? history.data?.identity
  function change(name: 'page' | 'historyPage', value: number) { const next = new URLSearchParams(search); next.set(name, String(value)); setSearch(next) }
  function toggleComparison() {
    setComparisonMode(current => !current)
    setComparisonStart(null)
  }
  function selectForComparison(item: RevisionMetadata) {
    if (!comparisonStart) {
      setComparisonStart(item)
      return
    }
    if (comparisonStart.id === item.id) {
      setComparisonStart(null)
      return
    }
    const before = comparisonStart.authorRevision < item.authorRevision ? comparisonStart : item
    const after = before.id === comparisonStart.id ? item : comparisonStart
    void navigate(compareHref(revisionKind, identityId, before.id, after.id, returnTo))
  }
  return <ContentDetailShell title="Revisions" action={identity ? <Link className={buttonVariants({ size: 'sm', variant: 'secondary' })} to={identityHref(identity, returnTo)}>{identityReturnLabel(returnTo)}</Link> : undefined}>
    <section className={panel} aria-labelledby="saved-revisions-heading"><SectionHeader
      id="saved-revisions-heading"
      title="Saved revisions"
      description={"Revision numbers record creation order; status describes each revision now. The complete list is newest authored first."}
      meta={<>{identity ? <><ContentKeyBadge value={identity.contentKey} /><Badge variant="neutral">{revisions.data?.total ?? 0} revisions</Badge></> : null}<OperatorFaqTrigger topicId="content-revisions" label="How revisions work" /></>}
    />
      {revisions.isPending ? <p>Loading revisions…</p> : revisions.isError ? <Notice variant="danger">Could not load revisions.</Notice> : <><RevisionStateSummary data={revisions.data} returnTo={returnTo ?? ''} /><div className="space-y-2"><SectionHeader
        level={3}
        title="All saved revisions · newest first"
        actions={revisions.data.total >= 2 ? <Button size="sm" variant={comparisonMode ? 'ghost' : 'secondary'} onClick={toggleComparison}><Icon name="compare" />{comparisonMode ? 'Cancel comparison' : 'Compare revisions'}</Button> : null}
      />{comparisonMode ? <Notice variant="neutral">{comparisonStart ? <>Revision {comparisonStart.authorRevision} selected. Choose one more revision.</> : 'Choose any two revision rows to compare them.'}</Notice> : null}<ul className="space-y-2">{revisions.data.items.map(item => <RevisionRow key={item.id} identity={revisions.data.identity} item={item} activeRevision={revisions.data.activeRevision} returnTo={returnTo} comparisonMode={comparisonMode} selectedForComparison={comparisonStart?.id === item.id} showSecondRevisionHint={comparisonStart !== null && comparisonStart.id !== item.id} onComparisonSelect={selectForComparison} />)}</ul></div><Pager page={revisions.data.page} total={revisions.data.total} limit={revisions.data.limit} onChange={value => change('page', value)} /></>}
    </section>
    <section className={panel} aria-labelledby="revision-history-heading"><SectionHeader
      id="revision-history-heading"
      title="Release history"
      meta={<Icon name="history" />}
    />
      {history.isPending ? <p>Loading release history…</p> : history.isError ? <Notice variant="danger">Could not load release history.</Notice> : history.data.items.length ? <><ul className="space-y-2">{history.data.items.map(event => <HistoryEvent key={event.id} identity={history.data.identity} event={event} returnTo={returnTo} />)}</ul><Pager page={history.data.page} total={history.data.total} limit={history.data.limit} onChange={value => change('historyPage', value)} /></> : <Notice variant="neutral">No import or lifecycle evidence is recorded for this identity. This can be expected for a legacy snapshot created before revision release history was introduced.</Notice>}
      <p><Link className="text-body underline text-text-secondary hover:text-text-primary" to="/admin/activity?family=configuration">Open the full Admin activity log</Link></p>
    </section>
  </ContentDetailShell>
}

function Metadata({ detail }: { detail: RevisionDetail }) {
  const item = detail.metadata
  return <section className={panel} aria-labelledby="revision-metadata-heading"><SectionHeader
    id="revision-metadata-heading"
    title="Revision details"
    meta={<><Badge variant="neutral">Revision {item.authorRevision}</Badge><ContentKeyBadge value={detail.identity.contentKey} /><ContentStatusBadge status={item.status} /><Badge variant="muted">{publicationLabel(item)}</Badge></>}
  />
    {!item.readable ? <Notice variant="danger">This application cannot read the retained payload ({item.payloadIssue ?? 'unknown payload issue'}). The evidence is retained, but its content cannot be rendered.</Notice> : null}
    <dl className="grid gap-4 text-body sm:grid-cols-2"><Value label="Revision ID"><code className="break-all text-caption">{item.id}</code></Value><Value label="Content identity ID"><code className="break-all text-caption">{detail.identity.id}</code></Value><Value label="Content hash"><code className="break-all text-caption">{item.contentHash}</code></Value><Value label="Schema / serializer">{item.schemaVersion} / {item.serializerVersion}</Value><Value label="Created">{formatActivityTimestamp(item.createdAt)} · {item.createdByActorId}</Value><Value label="Status changed">{formatActivityTimestamp(item.statusChangedAt)} · {item.statusChangedByActorId}</Value><Value label="First published">{item.sourceType === 'legacy-current-snapshot' ? 'Original publication not recorded' : item.firstPublishedAt ? `${formatActivityTimestamp(item.firstPublishedAt)} · ${item.firstPublishedByActorId}` : 'Never published'}</Value><Value label="Repository source">{item.sourcePath ?? 'Legacy database snapshot'}{item.sourceCommit ? ` · ${item.sourceCommit}` : ''}</Value></dl>
  </section>
}

function Value({ label, children }: { label: string; children: ReactNode }) { return <div><dt className="text-text-secondary">{label}</dt><dd className="mt-1 text-text-primary">{children}</dd></div> }

function Payload({ value }: { value: unknown }) {
  return <pre className="max-h-[48rem] overflow-auto whitespace-pre-wrap break-words rounded-token-card border border-border-default bg-surface-app p-4 font-mono text-caption"><code>{JSON.stringify(value, null, 2)}</code></pre>
}

type LifecycleDecision =
  | { action: RevisionLifecycleOperation; notice?: never }
  | { action?: never; notice: ReactNode }

function lifecycleDecision(
  detail: RevisionDetail,
  active: RevisionMetadata | null,
): LifecycleDecision {
  const identityLabel = detail.identity.kind === 'lesson' ? 'Scenario' : 'Tutor'
  if (detail.identity.status !== 'active') {
    return { notice: <Notice variant="neutral">This {identityLabel} is {detail.identity.status}. Its saved revisions remain available as history, but none can be made current from this page.</Notice> }
  }
  if (detail.metadata.status === 'active' && active?.id !== detail.metadata.id) {
    return { notice: <Notice variant="warning">The current revision could not be confirmed from this page snapshot. Refresh before taking any action.</Notice> }
  }
  if (detail.metadata.status === 'active') {
    if (detail.retirementImpacts.some(impact => impact.kind === 'onboarding_tutor_required')) {
      return { notice: <Notice variant="warning">Anna is required for every diagnostic call and cannot be retired. Fix her repository YAML and publish the corrected revision instead.</Notice> }
    }
    const requiredBy = detail.retirementImpacts
      .filter(impact => impact.kind === 'program_default_tutor_required')
      .flatMap(impact => impact.program ? [impact.program.label] : [])
    if (requiredBy.length > 0) {
      return { notice: <Notice variant="warning">This Tutor is the required default for {requiredBy.join(', ')}. Choose another default Tutor for {requiredBy.length === 1 ? 'that Program' : 'those Programs'} before retiring it.</Notice> }
    }
    const diagnosticFor = detail.retirementImpacts
      .filter(impact => impact.kind === 'program_diagnostic_required')
      .flatMap(impact => impact.program ? [impact.program.label] : [])
    if (diagnosticFor.length > 0) {
      return { notice: <Notice variant="warning">This diagnostic is required by {diagnosticFor.join(', ')} and cannot be retired. Fix its repository YAML and publish the corrected revision instead.</Notice> }
    }
    return { action: 'retire' }
  }
  if (detail.metadata.readable === false) {
    return { notice: <Notice variant="danger">This application cannot read this saved revision, so it cannot make the revision current.</Notice> }
  }
  if (detail.activationBlocker === 'review_marker') {
    return { notice: <Notice variant="danger">This saved revision contains a legacy review marker. It remains available as history but cannot be made current.</Notice> }
  }
  if (detail.activationBlocker === 'missing_presentation') {
    return { notice: <Notice variant="danger">This saved Tutor revision does not record presentation facets. It remains available as history but cannot be made current. Publish a reviewed repository revision with presentation facets instead.</Notice> }
  }
  if (detail.metadata.publicationState === 'unpublished_draft') {
    return { notice: <Notice variant="warning">This revision was imported but never published. Admin cannot publish authored content; correct or release its repository YAML through the normal deployment workflow.</Notice> }
  }
  if (detail.metadata.publicationState === 'abandoned_draft') {
    return { notice: <Notice variant="neutral">This revision was never published and has been superseded. It remains available as history and cannot be made current from Admin.</Notice> }
  }
  if (detail.metadata.publicationState === 'published_retired') {
    if (!active) return { action: 'restore' }
    if (detail.metadata.authorRevision < active.authorRevision) return { action: 'rollback' }
    if (detail.metadata.authorRevision > active.authorRevision) return { action: 'republish' }
  }
  return { notice: <Notice variant="warning">No lifecycle action is valid for this revision state. Refresh the page before deciding what to do.</Notice> }
}

function retirementImpactMessages(detail: RevisionDetail): string[] {
  return detail.retirementImpacts.flatMap((impact) => {
    if (impact.kind === 'program_level_unavailable' && impact.level) {
      return [`${impact.program?.label ?? 'Program'} · ${impact.level.label} will not be available for new calls or assignment. Unchanged catalog releases will remain blocked until this revision is restored or a corrected new YAML revision is published.`]
    }
    return []
  })
}

const lifecycleCopy: Record<RevisionLifecycleOperation, {
  button: (revision: number, currentRevision: number | null) => string
  panelTitle: (revision: number, currentRevision: number | null) => string
  panelDescription: (revision: number, currentRevision: number | null) => string
  title: (revision: number, currentRevision: number | null) => string
  result: (revision: number, currentRevision: number | null) => string
  reasonPlaceholder: (revision: number, currentRevision: number | null) => string
  icon: IconName
}> = {
  retire: {
    button: () => 'Retire content',
    panelTitle: () => 'Retire current revision',
    panelDescription: revision => `Retiring Revision ${revision} will leave this content with no current revision. New calls will not be able to select it. Calls already started keep their pinned revision.`,
    title: () => 'Retire content?',
    result: () => 'No revision will be available for new calls.',
    reasonPlaceholder: () => 'Explain why this content must be retired',
    icon: 'retire',
  },
  republish: {
    button: (revision, currentRevision) => `Replace Revision ${currentRevision ?? '?'} with Revision ${revision}`,
    panelTitle: (revision, currentRevision) => `Replace Revision ${currentRevision ?? '?'} with Revision ${revision}`,
    panelDescription: (revision, currentRevision) => `Revision ${revision} was previously published and is newer than current Revision ${currentRevision ?? '?'}. Re-publishing it will replace the current revision for new calls when the content's catalog context is available. Calls already started keep their pinned revision.`,
    title: (revision, currentRevision) => `Replace Revision ${currentRevision ?? '?'} with Revision ${revision}?`,
    result: (revision, currentRevision) => `Revision ${revision} will replace Revision ${currentRevision ?? '?'} as current. New calls can use it only when its catalog context is available.`,
    reasonPlaceholder: (revision, currentRevision) => `Explain why Revision ${revision} should replace Revision ${currentRevision ?? '?'}`,
    icon: 'success',
  },
  rollback: {
    button: (revision, currentRevision) => `Roll back from Revision ${currentRevision ?? '?'} to Revision ${revision}`,
    panelTitle: (revision, currentRevision) => `Roll back from Revision ${currentRevision ?? '?'} to Revision ${revision}`,
    panelDescription: (revision, currentRevision) => `Revision ${revision} is older than current Revision ${currentRevision ?? '?'}. Rolling back will make Revision ${revision} current for new calls when the content's catalog context is available. Calls already started keep their pinned revision.`,
    title: (revision, currentRevision) => `Roll back from Revision ${currentRevision ?? '?'} to Revision ${revision}?`,
    result: (revision, currentRevision) => `Revision ${revision} will replace Revision ${currentRevision ?? '?'} as current. New calls can use it only when its catalog context is available.`,
    reasonPlaceholder: (revision, currentRevision) => `Explain why Revision ${revision} should replace Revision ${currentRevision ?? '?'}`,
    icon: 'rollback',
  },
  restore: {
    button: revision => `Restore Revision ${revision}`,
    panelTitle: revision => `Restore Revision ${revision}`,
    panelDescription: revision => `This content currently has no current revision. Restoring Revision ${revision} will make it current for new calls when the content's catalog context is available.`,
    title: revision => `Restore Revision ${revision}?`,
    result: revision => `Revision ${revision} will become current. New calls can use it only when its catalog context is available.`,
    reasonPlaceholder: revision => `Explain why Revision ${revision} should be restored`,
    icon: 'restore',
  },
}

function lifecycleError(error: unknown): string {
  if (error instanceof AdminApiError && error.detail === 'content_revision_stale') {
    return 'Content changed since this page loaded. Refresh the page and inspect the latest release history before deciding again.'
  }
  if (error instanceof AdminApiError && error.detail === 'content_revision_operation_refused') {
    return 'This revision can no longer be used for that action. Refresh the page and inspect the latest release history.'
  }
  if (error instanceof AdminApiError && error.detail === 'content_revision_default_tutor_required') {
    return 'This Tutor is the default for an active Program and must keep a complete current revision. Change the Program default before retiring the Tutor.'
  }
  if (error instanceof AdminApiError && error.detail === 'content_revision_diagnostic_required') {
    return 'This diagnostic is required by an active Program and cannot be retired. Fix its repository YAML and publish the corrected revision instead.'
  }
  if (error instanceof AdminApiError && error.detail === 'content_revision_onboarding_tutor_required') {
    return 'Anna is required for every diagnostic call and cannot be retired. Fix her repository YAML and publish the corrected revision instead.'
  }
  return 'The content change could not be completed. Nothing changed; refresh the page before trying again.'
}

function RevisionLifecyclePanel({ detail, active }: { detail: RevisionDetail; active: RevisionMetadata | null }) {
  const decision = lifecycleDecision(detail, active)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const reasonId = useId()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ operation, submittedReason }: { operation: RevisionLifecycleOperation; submittedReason: string }) => adminContentClient.changeRevisionLifecycle(
      detail.identity.kind,
      detail.identity.id,
      detail.metadata.id,
      operation,
      {
        targetHash: detail.metadata.contentHash,
        expectedActive: active ? { revisionId: active.id, contentHash: active.contentHash } : null,
        reason: submittedReason,
      },
    ),
    onSuccess: async (result) => {
      setError(null)
      setReason('')
      setDialogOpen(false)
      setSuccess(result.toRevisionId
        ? `Revision ${detail.metadata.authorRevision} is now current. New-call availability still depends on its catalog context.`
        : 'No revision is current. New calls cannot select this content.')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.contentRevisionIdentity(detail.identity.kind, detail.identity.id) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.contentCatalog }),
        queryClient.invalidateQueries({ queryKey: queryKeys.contentCatalogDetails }),
        queryClient.invalidateQueries({ queryKey: queryKeys.content }),
      ])
    },
    onError: (failure) => {
      setSuccess(null)
      setError(lifecycleError(failure))
    },
  })

  const confirmation = success ? <p role="status" className="text-body font-medium text-text-primary">{success}</p> : null
  if (!decision.action) return <>{confirmation}{decision.notice}</>
  const action = decision.action
  const copy = lifecycleCopy[action]
  const currentRevision = active?.authorRevision ?? null
  const retirementMessages = action === 'retire' ? retirementImpactMessages(detail) : []
  return <>
    {confirmation}
    <HighConsequenceActionPanel
      dangerZone={action === 'retire'}
      title={copy.panelTitle(detail.metadata.authorRevision, currentRevision)}
      description={copy.panelDescription(detail.metadata.authorRevision, currentRevision)}
      error={error}
      onErrorDismiss={() => setError(null)}
    >
      {retirementMessages.length > 0 ? <Notice variant="warning"><span className="font-medium">This retirement also affects its catalog context:</span><ul className="mt-1 list-disc space-y-1 pl-5">{retirementMessages.map(message => <li key={message}>{message}</li>)}</ul></Notice> : null}
      <AlertDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (mutation.isPending) return
          setDialogOpen(open)
          if (!open) setReason('')
        }}
      >
        <AlertDialogTrigger asChild>
          <Button variant={action === 'retire' ? 'dangerOpen' : 'secondary'} disabled={mutation.isPending}>
            <Icon name={copy.icon} />{copy.button(detail.metadata.authorRevision, currentRevision)}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.title(detail.metadata.authorRevision, currentRevision)}</AlertDialogTitle>
            <p className="text-body font-medium text-text-primary">{detail.identity.label}</p>
            <AlertDialogDescription>
              Confirm the exact content and resulting revision state. This writes release history immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <dl data-slot="lifecycle-confirmation-summary" className="mt-5 overflow-hidden rounded-token-card border border-border-default bg-surface-app text-body">
            <div className="p-4">
              <Value label="Content"><span className="font-medium">{detail.identity.label}</span><span className="block text-caption text-text-secondary">{detail.identity.contentKey}</span></Value>
            </div>
            <div className="grid gap-3 border-t border-border-default p-4 sm:grid-cols-2">
              <Value label="Current revision">{active ? `Revision ${active.authorRevision}` : 'None — no revision is current'}</Value>
              <Value label="Selected revision">Revision {detail.metadata.authorRevision}</Value>
            </div>
            <div className="border-t border-border-default p-4">
              <Value label="After confirmation">{copy.result(detail.metadata.authorRevision, currentRevision)}</Value>
            </div>
            {retirementMessages.length > 0 ? <div className="border-t border-border-default p-4">
              <Value label="Affected catalog context"><ul className="list-disc space-y-1 pl-5">{retirementMessages.map(message => <li key={message}>{message}</li>)}</ul></Value>
            </div> : null}
          </dl>
          <div className="mt-5">
            <label htmlFor={reasonId} className="text-body font-medium">Reason for this change</label>
            <Textarea id={reasonId} className="mt-1 min-h-24" maxLength={1000} value={reason} onChange={event => setReason(event.target.value)} placeholder={copy.reasonPlaceholder(detail.metadata.authorRevision, currentRevision)} />
          </div>
          {error ? <Notice variant="danger">{error}</Notice> : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={mutation.isPending || reason.trim() === ''}
              onClick={(event) => {
                event.preventDefault()
                mutation.mutate({ operation: action, submittedReason: reason.trim() })
              }}
            >
              {mutation.isPending ? 'Applying…' : copy.button(detail.metadata.authorRevision, currentRevision)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </HighConsequenceActionPanel>
  </>
}

export function ContentRevisionDetailPage() {
  const { kind = 'lesson', identityId = '', revisionId = '' } = useParams<{ kind: RevisionKind; identityId: string; revisionId: string }>()
  const revisionKind = kind === 'tutor' ? 'tutor' : 'lesson'
  const [search] = useSearchParams()
  const returnTo = search.get('returnTo')
  const query = useQuery({
    queryKey: queryKeys.contentRevision(revisionKind, identityId, revisionId),
    queryFn: () => adminContentClient.revision(revisionKind, identityId, revisionId),
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const detail = query.data
  return <ContentDetailShell title={detail ? `Revision ${detail.metadata.authorRevision}` : 'Revision'} action={detail ? <Link className={buttonVariants({ size: 'sm', variant: 'secondary' })} to={revisionHref(revisionKind, identityId, undefined, returnTo)}>All revisions</Link> : undefined}>
    {query.isPending ? <p>Loading revision…</p> : query.isError ? <Notice variant="danger">This retained revision could not be loaded.</Notice> : detail ? <><Metadata detail={detail} />{detail.identity.kind === 'tutor' ? <TutorPresentationPanel presentation={readRevisionTutorPresentation(detail.payload, detail.metadata.readable)} /> : null}<section className={panel} aria-labelledby="saved-payload-heading"><SectionHeader id="saved-payload-heading" title="Saved authored payload" />{detail.payload === null ? <Notice variant="danger">The saved payload is unavailable to this application version.</Notice> : <Payload value={detail.payload} />}</section><RevisionLifecyclePanel detail={detail} active={detail.activeRevision} /></> : null}
  </ContentDetailShell>
}

type Difference = { path: string; before: unknown; after: unknown; kind: 'added' | 'removed' | 'changed' }
function authoredComparisonValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(authoredComparisonValue)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, child]) => child !== null && child !== undefined)
        .map(([key, child]) => [key, authoredComparisonValue(child)]),
    )
  }
  return value
}
function differences(before: unknown, after: unknown, path = ''): Difference[] {
  if (JSON.stringify(before) === JSON.stringify(after)) return []
  if (before && after && typeof before === 'object' && typeof after === 'object' && !Array.isArray(before) && !Array.isArray(after)) {
    const left = before as Record<string, unknown>; const right = after as Record<string, unknown>
    return [...new Set([...Object.keys(left), ...Object.keys(right)])].flatMap(key => differences(left[key], right[key], path ? `${path}.${key}` : key))
  }
  return [{ path: path || 'payload', before, after, kind: before === undefined ? 'added' : after === undefined ? 'removed' : 'changed' }]
}
function display(value: unknown) { return value === undefined ? 'Not present' : typeof value === 'string' ? value : JSON.stringify(value, null, 2) }
function unchangedFieldCount(before: unknown, after: unknown): number {
  if (JSON.stringify(before) === JSON.stringify(after)) return 1
  if (before && after && typeof before === 'object' && typeof after === 'object' && !Array.isArray(before) && !Array.isArray(after)) {
    const left = before as Record<string, unknown>; const right = after as Record<string, unknown>
    return [...new Set([...Object.keys(left), ...Object.keys(right)])].reduce((count, key) => count + unchangedFieldCount(left[key], right[key]), 0)
  }
  return 0
}

export function ContentRevisionComparePage() {
  const { kind = 'lesson', identityId = '' } = useParams<{ kind: RevisionKind; identityId: string }>()
  const revisionKind = kind === 'tutor' ? 'tutor' : 'lesson'
  const [search] = useSearchParams()
  const from = search.get('from') ?? ''
  const to = search.get('to') ?? ''
  const returnTo = search.get('returnTo')
  const query = useQuery({ queryKey: queryKeys.contentRevisionComparison(revisionKind, identityId, from, to), queryFn: () => adminContentClient.compareRevisions(revisionKind, identityId, from, to), enabled: Boolean(from && to) })
  const data = query.data
  const beforePayload = data ? authoredComparisonValue(data.before.payload) : null
  const afterPayload = data ? authoredComparisonValue(data.after.payload) : null
  const changes = data ? differences(beforePayload, afterPayload) : []
  const unchanged = data ? unchangedFieldCount(beforePayload, afterPayload) : 0
  return <ContentDetailShell title="Compare revisions" action={<Link className={buttonVariants({ size: 'sm', variant: 'secondary' })} to={revisionHref(revisionKind, identityId, undefined, returnTo)}>All revisions</Link>}>
    {!query.isError && data?.identity.kind === 'tutor' ? <TutorPresentationComparison before={readRevisionTutorPresentation(data.before.payload, data.before.metadata.readable)} after={readRevisionTutorPresentation(data.after.payload, data.after.metadata.readable)} beforeRevision={data.before.metadata.authorRevision} afterRevision={data.after.metadata.authorRevision} /> : null}
    {!from || !to ? <Notice variant="neutral">Choose two revisions from the revision list to compare them.</Notice> : query.isPending ? <p>Comparing revisions…</p> : query.isError ? <Notice variant="danger">These revisions could not be compared. Both must belong to this content identity and be readable.</Notice> : data ? <section className={panel} aria-labelledby="revision-differences-heading">
      <SectionHeader
        id="revision-differences-heading"
        title="Authored field changes"
        meta={<><ContentKeyBadge value={data.identity.contentKey} /><Badge variant="neutral">{changes.length} changed</Badge><Badge variant="muted">{unchanged} unchanged</Badge></>}
      />
      {!data.before.metadata.readable || !data.after.metadata.readable ? <Notice variant="danger">At least one retained payload is unreadable, so an exact comparison is unavailable.</Notice> : <>
        <div className="grid gap-3 sm:grid-cols-2">
          <div data-testid="comparison-revision-heading" className="flex flex-wrap items-center gap-2 rounded-token-card border border-border-default bg-surface-app px-4 py-3"><span className="font-semibold">Revision {data.before.metadata.authorRevision}</span><ContentStatusBadge status={data.before.metadata.status} /></div>
          <div data-testid="comparison-revision-heading" className="flex flex-wrap items-center gap-2 rounded-token-card border border-border-default bg-surface-app px-4 py-3"><span className="font-semibold">Revision {data.after.metadata.authorRevision}</span><ContentStatusBadge status={data.after.metadata.status} /></div>
        </div>
        {changes.length ? <div className="space-y-3">{changes.map(change => <article key={change.path} className="rounded-token-card border border-border-default bg-surface-app p-4"><div className="flex flex-wrap items-center gap-2"><h3 className="font-mono text-caption font-semibold">{change.path}</h3><Badge variant={change.kind === 'added' ? 'success' : change.kind === 'removed' ? 'warning' : 'neutral'}>{change.kind}</Badge></div><div className="mt-3 grid gap-3 sm:grid-cols-2"><div><p className="text-caption text-text-secondary sm:hidden">Revision {data.before.metadata.authorRevision}</p><pre className="mt-1 whitespace-pre-wrap break-words text-caption">{display(change.before)}</pre></div><div><p className="text-caption text-text-secondary sm:hidden">Revision {data.after.metadata.authorRevision}</p><pre className="mt-1 whitespace-pre-wrap break-words text-caption">{display(change.after)}</pre></div></div></article>)}</div> : <Notice variant="neutral">The saved authored payloads are identical; all authored fields are unchanged.</Notice>}
      </>}
    </section> : null}
  </ContentDetailShell>
}
